import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  Wrench, 
  Headphones, 
  Package, 
  Clipboard, 
  Settings, 
  Cpu,
  CheckCircle,
  Clock,
  Shield
} from "lucide-react";
import { services } from "../data/mock";

const iconMap = {
  wrench: Wrench,
  headset: Headphones,
  package: Package,
  clipboard: Clipboard,
  settings: Settings,
  cpu: Cpu
};

export const Services = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <Badge className="mb-4 bg-amber-500/20 text-amber-300 border-amber-500/30">Our Services</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Comprehensive Support for Your Manufacturing Success
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              From installation to maintenance, we provide end-to-end support to ensure your machines deliver optimal performance throughout their lifecycle.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Complete Lifecycle Support
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We stand behind our machines with comprehensive services that maximize uptime and productivity.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card key={service.id} className="group hover:shadow-2xl transition-all duration-300 border-t-4 border-amber-600">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-blue-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">{service.title}</h3>
                    <p className="text-slate-600 leading-relaxed mb-4">{service.description}</p>
                    {service.details && (
                      <ul className="space-y-2">
                        {service.details.map((detail) => (
                          <li key={detail} className="text-sm text-slate-600 flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Details */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto space-y-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">
                  Professional Machine Installation
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Our experienced engineers handle complete installation and commissioning of all machinery. We ensure proper setup, calibration, and testing before handover, minimizing your time-to-production.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-slate-600">Site survey and preparation guidance</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-slate-600">Complete mechanical and electrical installation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-slate-600">Precision calibration and testing</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-slate-600">Operator training and documentation</span>
                  </li>
                </ul>
              </div>
              <div>
                <img
                  src="https://images.unsplash.com/photo-1647427060118-4911c9821b82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxtYW51ZmFjdHVyaW5nJTIwZXF1aXBtZW50fGVufDB8fHx8MTc3NTgwNTQ4OHww&ixlib=rb-4.1.0&q=85"
                  alt="Installation service"
                  className="w-full h-80 object-cover rounded-xl shadow-lg"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <img
                  src="https://images.unsplash.com/photo-1717386255773-a456c611dc4e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxtYW51ZmFjdHVyaW5nJTIwZXF1aXBtZW50fGVufDB8fHx8MTc3NTgwNTQ4OHww&ixlib=rb-4.1.0&q=85"
                  alt="Support service"
                  className="w-full h-80 object-cover rounded-xl shadow-lg"
                />
              </div>
              <div className="order-1 md:order-2">
                <h2 className="text-3xl font-bold text-slate-900 mb-4">
                  24/7 After-Sales Support
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  We provide round-the-clock technical assistance to keep your production running smoothly. Our support team is always ready to help with troubleshooting, maintenance guidance, and emergency repairs.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-slate-600">24/7 phone and email support</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-slate-600">Remote diagnostics and troubleshooting</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-slate-600">On-site emergency repair services</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-slate-600">Priority response for critical issues</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">
                  Preventive Maintenance Programs
                </h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  Maximize machine lifespan and minimize unexpected downtime with our comprehensive preventive maintenance programs. Regular inspections and timely servicing keep your equipment running at peak efficiency.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-slate-600">Scheduled maintenance visits</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-slate-600">Comprehensive machine health checks</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-slate-600">Parts replacement and lubrication</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-slate-600">Detailed maintenance reports</span>
                  </li>
                </ul>
              </div>
              <div>
                <img
                  src="https://images.unsplash.com/photo-1758271141001-e4ff47f2b1c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxtYW51ZmFjdHVyaW5nJTIwZXF1aXBtZW50fGVufDB8fHx8MTc3NTgwNTQ4OHww&ixlib=rb-4.1.0&q=85"
                  alt="Maintenance service"
                  className="w-full h-80 object-cover rounded-xl shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Features */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Why Choose Our Services
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Industry-leading support backed by decades of experience and commitment to customer success.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <Card className="border-t-4 border-green-600">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Rapid Response</h3>
                <p className="text-slate-600">
                  Average response time of less than 2 hours for critical support requests with nationwide service network.
                </p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-blue-600">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Genuine Parts</h3>
                <p className="text-slate-600">
                  Only authentic spare parts used in all repairs and maintenance, ensuring reliability and longevity.
                </p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-purple-600">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">Expert Technicians</h3>
                <p className="text-slate-600">
                  Factory-trained engineers with deep product knowledge and years of hands-on experience.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Spare Parts Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 text-center">
              Genuine Spare Parts Supply
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8 text-center">
              We maintain a comprehensive inventory of genuine spare parts for all our machines, ensuring quick availability and minimal downtime for your operations.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Common Spare Parts</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-amber-600 rounded-full" />
                      Doctor blades and coating rollers
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-amber-600 rounded-full" />
                      Slitting knives and holders
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-amber-600 rounded-full" />
                      Pneumatic shafts and chucks
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-amber-600 rounded-full" />
                      Drive belts and bearings
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-amber-600 rounded-full" />
                      Sensors and control panels
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Parts Benefits</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      100% genuine OEM parts
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      Warranty coverage on all parts
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      Quick delivery across India
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      Competitive pricing
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      Expert installation support
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
